import React, { useContext, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { RoutingStep } from 'router/models/Routes';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import StepsContext from 'providers/Steps/Steps.context';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  MEDIA_PACKAGE_PATH,
  PACKAGES_WITHOUT_MEDIA,
} from 'providers/Steps/Steps.provider';
import useCallbackRef from 'hooks/useCallbackRef';
import ListingContext from '../../providers/Listing/Listing.context';

import './ListingCreationStepper.styles.scss';
import './ListingCreationStepper.styles.responsive.scss';

export type ListingCreationStepperProps = {
  className?: string;
  steps?: RoutingStep[];
  currentStep?: number;
  completedSteps?: number;
};

const ListingCreationStepper: React.FC<ListingCreationStepperProps> = (
  props,
) => {
  const {
    currentStep: conCurrentStep,
    steps: conSteps,
    completedSteps: conCompletedSteps,
  } = useContext(StepsContext);
  const { listing } = useContext(ListingContext);
  const {
    className,
    steps = conSteps,
    completedSteps = conCompletedSteps,
    currentStep = conCurrentStep,
  } = props;
  const [container, containerRef] = useCallbackRef();
  const firstElement = useRef<HTMLElement>();
  const lastElement = useRef<HTMLElement>();

  const isPhablet = useMatchMedia('isPhablet');
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const classes = classNames('listing-creation-stepper', className);

  const translateWidthPercentage = useMemo(
    () => 100 / (isPhablet ? steps.length : steps.length - 1),
    [isPhablet, steps.length],
  );

  const renderSteps = useMemo(
    () =>
      !!steps.length &&
      steps.map(({ path, labelId }, index) => {
        const disabled =
          path === MEDIA_PACKAGE_PATH &&
          PACKAGES_WITHOUT_MEDIA.includes(listing?.listingPackage);
        const isActive = pathname.includes(path);

        return (
          <li key={path}>
            <p
              ref={(ref) => {
                if (index === 0) {
                  firstElement.current = ref;
                } else if (index === steps.length - 1) {
                  lastElement.current = ref;
                }
              }}
              className={classNames({
                completed: completedSteps + 1 > index,
                previous: currentStep > index,
                active: isActive || currentStep === index,
                disabled: completedSteps < index || disabled,
              })}
            >
              <span>{index + 1}</span>
              <span className="popup">{t(labelId)}</span>
            </p>
          </li>
        );
      }),
    [completedSteps, currentStep, listing?.listingPackage, pathname, steps, t],
  );

  useEffect(() => {
    if (!container || !firstElement.current) return;

    container.style.setProperty(
      '--left-progress',
      `${firstElement.current.clientWidth / 2}px`,
    );
    container.style.setProperty(
      '--right-progress',
      `${lastElement.current.clientWidth / 2}px`,
    );
  }, [container]);

  return (
    <nav className={classes} ref={containerRef}>
      <div>
        <div className="listing-creation-stepper__progress">
          <div
            style={{
              transform: `translate3d(-${
                100 - translateWidthPercentage * completedSteps
              }%,0,0)`,
            }}
          />
          <div
            style={{
              transform: `translate3d(-${
                100 -
                translateWidthPercentage *
                  (isPhablet ? currentStep + 1 : currentStep)
              }%,0,0)`,
            }}
          />
        </div>
        <ul>{renderSteps}</ul>
      </div>
    </nav>
  );
};

export default ListingCreationStepper;
